import { useState, useEffect } from 'react';
import { Comment } from '../types';
import { commentApi } from '../services/api';
import { MessageCircle, Trash2, Send } from 'lucide-react';
import { useTaskStore } from '../store/taskStore';

interface CommentSectionProps {
  taskId: number;
}

export default function CommentSection({ taskId }: CommentSectionProps) {
  const { addToast } = useTaskStore();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [authorName, setAuthorName] = useState('');
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch comments
  useEffect(() => {
    fetchComments();
  }, [taskId]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const data = await commentApi.getComments(taskId);
      setComments(data);
    } catch (error: any) {
      addToast({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!authorName.trim() || !content.trim()) {
      addToast({ type: 'error', message: 'Lütfen tüm alanları doldurun' });
      return;
    }

    setIsSubmitting(true);
    try {
      const newComment = await commentApi.createComment(taskId, {
        author_name: authorName.trim(),
        content: content.trim(),
      });
      setComments([newComment, ...comments]);
      setContent('');
      addToast({ type: 'success', message: 'Yorum eklendi' });
    } catch (error: any) {
      addToast({ type: 'error', message: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (commentId: number) => {
    if (!confirm('Bu yorumu silmek istediğinizden emin misiniz?')) return;

    try {
      await commentApi.deleteComment(commentId);
      setComments(comments.filter(c => c.id !== commentId));
      addToast({ type: 'success', message: 'Yorum silindi' });
    } catch (error: any) {
      addToast({ type: 'error', message: error.message });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('tr-TR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <MessageCircle className="w-5 h-5 text-primary-500" />
        <h3 className="text-lg font-semibold text-gray-900">
          Yorumlar {comments.length > 0 && `(${comments.length})`}
        </h3>
      </div>

      {/* Comment Form */}
      <form onSubmit={handleSubmit} className="bg-soft-gray rounded-lg p-4 space-y-3">
        <div>
          <input
            type="text"
            value={authorName}
            onChange={(e) => setAuthorName(e.target.value)}
            placeholder="İsminiz"
            className="w-full px-3 py-2 border border-soft-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            disabled={isSubmitting}
          />
        </div>
        <div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Yorumunuzu yazın..."
            rows={3}
            className="w-full px-3 py-2 border border-soft-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none text-sm"
            disabled={isSubmitting}
          />
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting || !authorName.trim() || !content.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            <Send className="w-4 h-4" />
            {isSubmitting ? 'Gönderiliyor...' : 'Gönder'}
          </button>
        </div>
      </form>

      {/* Comments List */}
      {loading ? (
        <div className="text-center py-8 text-soft-muted">Yorumlar yükleniyor...</div>
      ) : comments.length === 0 ? (
        <div className="text-center py-8 text-soft-muted">
          <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Henüz yorum yok. İlk yorumu siz yapın!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {comments.map((comment) => (
            <div
              key={comment.id}
              className="bg-white rounded-lg p-4 shadow-soft hover:shadow-soft-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-medium text-gray-900">{comment.author_name}</p>
                  <p className="text-xs text-soft-muted">{formatDate(comment.created_at)}</p>
                </div>
                <button
                  onClick={() => handleDelete(comment.id)}
                  className="text-soft-muted hover:text-red-500 transition-colors"
                  title="Yorumu sil"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <p className="text-soft-text whitespace-pre-wrap">{comment.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
